import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";

const ACCIDENT_TYPE_OPTIONS = [
  { value: "", label: "— select —" },
  { value: "auto", label: "Auto" },
  { value: "motorcycle", label: "Motorcycle" },
  { value: "public_transportation", label: "Public Transportation" },
  { value: "truck", label: "Truck" },
  { value: "commercial_vehicle", label: "Commercial Vehicle" },
  { value: "pedestrian", label: "Pedestrian" },
  { value: "bicycle", label: "Bicycle" },
  { value: "rideshare", label: "Rideshare (Uber/Lyft)" },
  { value: "other", label: "Other" },
];

const INITIAL_FORM = {
  first_name: "", last_name: "", phone: "", email: "",
  address: "", city: "", state: "", zip: "", county: "",
  accident_date: "", trustedform_url: "",
  campaign_id: "", affiliate_id: "", is_test: false,
  sub_1: "", sub_2: "", sub_3: "", sub_4: "", sub_5: "",
  notes: "",
  // PI case details
  accident_type: "", incident_date: "",
  at_fault: "", currently_represented: "",
  physically_injured: "", medical_treatment: "",
  phone_verified: "",
  injuries: "", accident_description: "", location: "",
  report_number: "", vin: "", driver_license: "",
};

export default function NewLead() {
  const nav = useNavigate();
  const [form, setForm] = useState(INITIAL_FORM);
  const [affiliates, setAffiliates] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/organizations?type=affiliate").then((r) => setAffiliates(r.data.organizations));
    api.get("/campaigns?active_only=true").then((r) => setCampaigns(r.data.campaigns));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => (payload[k] === "" || payload[k] === null) && delete payload[k]);
      const { data } = await api.post("/leads/manual", payload);
      toast.success(`Lead ${data.id.slice(0, 8)} created`);
      nav(`/leads/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create lead");
    } finally {
      setLoading(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="max-w-3xl">
      <div className="label-mono">Manual entry</div>
      <h1 className="font-chivo font-black text-3xl uppercase tracking-tighter">New lead</h1>

      <form onSubmit={submit} className="brut-card mt-6 p-6 grid grid-cols-2 gap-4">
        <Field label="First name" required v={form.first_name} onChange={set("first_name")} testId="nl-first" />
        <Field label="Last name" required v={form.last_name} onChange={set("last_name")} testId="nl-last" />
        <Field label="Phone" v={form.phone} onChange={set("phone")} testId="nl-phone" />
        <Field label="Email" v={form.email} onChange={set("email")} type="email" testId="nl-email" />
        <Field label="Address" v={form.address} onChange={set("address")} testId="nl-address" />
        <Field label="City" v={form.city} onChange={set("city")} testId="nl-city" />
        <Field label="State (2-letter)" v={form.state} onChange={set("state")} testId="nl-state" />
        <Field label="ZIP" v={form.zip} onChange={set("zip")} testId="nl-zip" />
        <Field label="County" v={form.county} onChange={set("county")} testId="nl-county" />
        <Field label="Accident date" v={form.accident_date} onChange={set("accident_date")} type="date" testId="nl-accident" />

        <div>
          <label className="label-mono">Campaign</label>
          <select data-testid="nl-campaign" className="w-full border border-black px-2 py-1.5 mt-1" value={form.campaign_id} onChange={set("campaign_id")}>
            <option value="">— none —</option>
            {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label-mono">Affiliate (source)</label>
          <select data-testid="nl-affiliate" className="w-full border border-black px-2 py-1.5 mt-1" value={form.affiliate_id} onChange={set("affiliate_id")}>
            <option value="">— none —</option>
            {affiliates.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>

        <Field label="TrustedForm URL" v={form.trustedform_url} onChange={set("trustedform_url")} testId="nl-tf" span={2} />

        {/* -------- PI case details -------- */}
        <div className="col-span-2 border-t border-black pt-4 mt-2">
          <div className="label-mono mb-2">Case details (personal injury qualifiers)</div>
        </div>

        <div>
          <label className="label-mono">Accident type</label>
          <select data-testid="nl-accident-type" className="w-full border border-black px-2 py-1.5 mt-1"
                  value={form.accident_type} onChange={set("accident_type")}>
            {ACCIDENT_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <Field label="Incident date" v={form.incident_date} onChange={set("incident_date")} type="date" testId="nl-incident-date" />

        <YesNoField label="At fault" testId="nl-at-fault" value={form.at_fault} onChange={set("at_fault")} hint="must be No" />
        <YesNoField label="Currently represented" testId="nl-currently-represented" value={form.currently_represented} onChange={set("currently_represented")} hint="must be No" />
        <YesNoField label="Physically injured" testId="nl-physically-injured" value={form.physically_injured} onChange={set("physically_injured")} hint="must be Yes" />
        <YesNoField label="Medical treatment" testId="nl-medical-treatment" value={form.medical_treatment} onChange={set("medical_treatment")} hint="must be Yes" />
        <YesNoField label="Phone verified" testId="nl-phone-verified" value={form.phone_verified} onChange={set("phone_verified")} />

        <Field label="Accident location" v={form.location} onChange={set("location")} testId="nl-location" />
        <Field label="Report number" v={form.report_number} onChange={set("report_number")} testId="nl-report-number" />
        <Field label="VIN" v={form.vin} onChange={set("vin")} testId="nl-vin" />
        <Field label="Driver license" v={form.driver_license} onChange={set("driver_license")} testId="nl-dl" />

        <div className="col-span-2">
          <label className="label-mono">Injuries</label>
          <textarea data-testid="nl-injuries" rows={2}
            className="w-full border border-black px-3 py-2 mt-1"
            value={form.injuries} onChange={set("injuries")} />
        </div>

        <div className="col-span-2">
          <label className="label-mono">Accident / case description</label>
          <textarea data-testid="nl-accident-description" rows={3}
            className="w-full border border-black px-3 py-2 mt-1"
            value={form.accident_description} onChange={set("accident_description")} />
        </div>

        {/* -------- Attribution / metadata -------- */}
        <div className="col-span-2 border-t border-black pt-4 mt-2">
          <div className="label-mono mb-2">Sub IDs & notes</div>
        </div>

        <div className="col-span-2 grid grid-cols-5 gap-3">
          <Field label="Sub 1" v={form.sub_1} onChange={set("sub_1")} testId="nl-sub1" />
          <Field label="Sub 2" v={form.sub_2} onChange={set("sub_2")} testId="nl-sub2" />
          <Field label="Sub 3" v={form.sub_3} onChange={set("sub_3")} testId="nl-sub3" />
          <Field label="Sub 4" v={form.sub_4} onChange={set("sub_4")} testId="nl-sub4" />
          <Field label="Sub 5" v={form.sub_5} onChange={set("sub_5")} testId="nl-sub5" />
        </div>

        <div className="col-span-2">
          <label className="label-mono">Notes</label>
          <textarea data-testid="nl-notes" rows={3}
            className="w-full border border-black px-3 py-2 mt-1"
            value={form.notes} onChange={set("notes")} />
        </div>

        <label className="col-span-2 inline-flex items-center gap-2 text-xs">
          <input type="checkbox" data-testid="nl-test"
            checked={form.is_test} onChange={(e) => setForm({ ...form, is_test: e.target.checked })} />
          <span className="uppercase tracking-widest">Mark as test lead</span>
        </label>

        <div className="col-span-2 flex justify-end gap-3 pt-2 border-t border-black">
          <button
            type="submit"
            data-testid="nl-submit"
            disabled={loading}
            className="bg-black text-white px-4 py-2 uppercase text-xs tracking-widest font-bold border border-black hover:translate-x-[2px] hover:translate-y-[2px] hover:brut-shadow-sm transition-transform duration-100"
          >
            {loading ? "Saving…" : "Create lead"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, v, onChange, type = "text", required = false, testId, span = 1 }) {
  return (
    <div className={span === 2 ? "col-span-2" : ""}>
      <label className="label-mono">{label}{required && "*"}</label>
      <input
        data-testid={testId}
        type={type} required={required} value={v || ""} onChange={onChange}
        className="w-full border border-black px-3 py-2 mt-1"
      />
    </div>
  );
}

function YesNoField({ label, value, onChange, testId, hint }) {
  return (
    <div>
      <label className="label-mono">
        {label}{hint && <span className="text-zinc-500 normal-case tracking-normal"> · {hint}</span>}
      </label>
      <select
        data-testid={testId}
        value={value || ""}
        onChange={onChange}
        className="w-full border border-black px-2 py-1.5 mt-1"
      >
        <option value="">— select —</option>
        <option value="Yes">Yes</option>
        <option value="No">No</option>
      </select>
    </div>
  );
}
